// Ultimate Card Game (UCG) by Clord - Cards Database
// Extracted and balanced directly from MapScript.galaxy & GameStrings.txt
//
// Schema reference (verified against all entries below - see
// docs/CARD_AUTHORING_GUIDE.md for the step-by-step "adding a new card"
// checklist, and docs/INVARIANTS.md for why these rules exist):
//
// UNIVERSAL (every card, all 96 entries as of this writing):
//   tier: 1-3 (draw-pool weighting, no longer a pity/rarity mechanic - see
//     CardSystem.drawCard()'s single-player path, which draws uniformly)
//   rarity: "Common" | "Uncommon" | "Rare" | "Epic" (display/flavor only)
//   id: number - unique WITHIN one (tier, rarity) pair, not globally. Two
//     cards of different tier/rarity may share the same id.
//   enabled: bool - false permanently excludes a card from the draw pool
//     (getActiveCards() in js/data/decks.js) EXCEPT type:"Melt" result
//     cards (see below), which stay drawable=false forever by design but
//     must still appear in the admin balance table (api/admin.php's
//     'cards' handler explicitly keeps type:"Melt" cards even when
//     enabled:false - don't "fix" that by re-enabling them).
//   name: string, must be unique - looked up by name in several places
//     (CARDS_DATA.find(c => c.name === ...)), never by id alone.
//   race: "Human" | "Sul"
//   caste: "Loaf" | "Pillar" | "Lock" | "Vulture" (for Sul units; defines rank in symbiosis)
//   cost: number (Plasma gas to play)
//   unitType: string - the internal type key consumed by the renderer
//     (js/engine/render/unitSprites.js's sprite lookup) and by
//     cardData.unitType === 'X' checks scattered across js/engine/unit.js
//     for unique per-unit-type abilities. Must match an existing sprite
//     entry unless the card is a Spell/Building (which don't render a unit).
//   images: string[] - filenames under assets/cards/, first entry is the
//     card art shown in hand.
//   description: string - shown on the card in the UI.
//
// OPTIONAL, universally applicable:
//   lore: string - flavor text / storytelling text (e.g. quotes, background),
//     rendered in italics in a separate text block below the card description
//     in zoom view (analogous to Magic: The Gathering flavor text).
//   type: "Structure" | "Melt" - an alternative/legacy way to mark a
//     Structure (equivalent to isStructure: true, both are checked with OR
//     everywhere - e.g. CardSystem.isStructureInHand()) or a triplet-fusion
//     RESULT card (CardSystem.checkDrilling() - a Melt card is never drawn
//     directly, only produced by fusing 3 of its "ingredient" card).
//   statsText: string - short stat-line shown under the description.
//   sound: string - bark/sfx key, see js/engine/audio.js.
//
// COMBAT STATS (present on actual on-field units, absent on pure Spells):
//   hp, damage, attackSpeed, range, speed, armor, accuracy: numbers.
//   targetsAir / targetsGround: bool, per-unit opt-out (e.g. Panzer has no
//     air attack) - see Battlefield.findBestTarget()'s filtering.
//   isAir: bool - flight/ground-collision + targeting eligibility.
//   attributes: string[] from {"Light","Armored","Massive","Biological",
//     "Mechanical","Psionic","Structure","Heroic"} - the counter-system
//     tags, read all over js/engine/ (buildings.js's repair eligibility,
//     unit.js's clone/slow immunity, battlefield.js's heal eligibility).
//     Omit entirely if none apply (a plain unit needs no tags).
//   elementType: "Ground" | "Air", projectileType: "Bullet" | "Energy" |
//     "Grenade" | "Rocket" - drive deflection-rating interactions; "Energy"
//     projectiles always bypass Deflection Rating (see Unit.takeDamage()).
//   deflectionRating, splashRadius, splashLine, attackCount, reloadSkipCount,
//   bounceCount, slowOnHit, isKamikaze, isCloaked, isDetector,
//   detectionRange, isHealer, healRate, regenRate, shields: all
//   special-case combat modifiers - grep js/engine/unit.js for the exact
//   field name to see what it gates before adding a new one, most are
//   only used by 1-3 cards.
//   targetAccuracyDebuff: number (0-1) - flat amount subtracted from an
//     attacker's own accuracy when its target is THIS unit, read from
//     target.cardData inside Unit.resolveNewCombatAttack() before the
//     miss-roll, clamped like the base roll. Self-only (no radius/aura),
//     only affects attackers on the new combat schema (hasNewCombatSchema,
//     i.e. cards with a projectileType - the entire active roster as of
//     this writing). Introduced for Wheelie Bomber.
//   dualWeapon, burstDuration, reloadDuration, ejectPilotOnDeath:
//     special-case attack & death modifiers for multi-weapon aircraft and
//     pilot ejection mechanics (introduced for A9 Piranha).
//   hasGroundBomb, bombDamage, bombSplashRadius, reverseSpeed:
//     one-time heavy bomb drop against ground units/structures and VTOL
//     backward kiting speed (introduced for A9 Pigeon).
//
// SPELL cards: isSpell: true + spellEffect: string (matched in
//   CardSystem.executeSpell()'s dispatch - add a new spellEffect string
//   there when adding a new spell card). If the effect needs a target unit,
//   read it from executeSpell()'s targetUnitId parameter ONLY - never
//   battlefield.selectedUnit, see docs/INVARIANTS.md #1 - and set
//   spellRequiresTarget: true on the card so the generic determinism test
//   in tests/netcode_test.mjs picks it up automatically (see clone_unit/
//   upper_hand below for the pattern).
//

// MODULE cards: isModule: true + moduleId: string (looked up in
//   js/engine/modules.js's MODULE_DEFS) + type: "Module". A module is a base
//   EXTENSION, not a field unit: playing it lowers a steel-frame module onto
//   the team's main building, stacks it there (max MODULE_MAX) and adds its
//   `hp` to the base 1:1 - HP is a per-module attribute, so every module may
//   bring a different amount. It creates no Unit, cannot be attacked or
//   destroyed individually, and is spent for the rest of the match the
//   moment it is played (CardSystem.isModuleUnavailable() blocks re-drawing
//   it permanently, unlike a Structure whose block lifts when it dies).
//   Module cards therefore carry NO unit-only combat fields (speed,
//   isStructure, attributes, deflectionRating, elementType, targetsAir/
//   targetsGround) - only `hp`, `cost` and whatever its own mechanic needs.
//   See docs/MODULE_SYSTEM_PLAN.md.
//
// VESTIGIAL - present on some older cards, read by NOTHING in js/engine/ or
// js/ui/ (verified by repo-wide grep) - a leftover from the pre-"Neustart
// Marine Null" CO-Power/upgrade-lines system. Do NOT add these to new
// cards; safe to ignore if you see them on an existing one:
//   attackBoostCost, defenseBoostCost, bonusLight, bonusArmored,
//   bonusBiological, bonusMassive

export const BALANCE_HASH = '96323fda';

export const CARDS_DATA = [
  // ============================================================================
  // 1. AKTIVES ROSTER (LIVE IM SPIEL — DYNAMISCH ERWEITERBAR)
  // Einheiten, Spells und Strukturen, die aktiv gezogen, gespielt und simuliert
  // werden. Neue Einheiten für das Spiel werden hier angelegt.
  // ============================================================================
  {
      "tier": 1,
      "rarity": "Common",
      "id": 1,
      "enabled": true,
      "name": "Marine",
      "race": "Human",
      "cost": 5,
      "type": "Unit",
      "unitType": "Marine",
      "hp": 45,
      "damage": 6,
      "attackSpeed": 0.4,
      "range": 5,
      "speed": 2.25,
      "accuracy": 0.8,
      "deflectionRating": 0,
      "projectileType": "Bullet",
      "elementType": "Ground",
      "description": "Basic infantry unit",
      "lore": "Why me?",
      "sound": "Marine_Ready",
      "images": [
          "Marine_1.png"
      ]
  },

  {
      "tier": 1,
      "rarity": "Uncommon",
      "id": 3,
      "enabled": true,
      "name": "Tesla Walker",
      "race": "Human",
      "cost": 15,
      "type": "Unit",
      "unitType": "TeslaWalker",
      "hp": 150,
      "damage": 30,
      "attackSpeed": 3,
      "range": 8,
      "speed": 1.5,
      "accuracy": 1,
      "deflectionRating": 0.2,
      "projectileType": "Energy",
      "elementType": "Ground",
      "deflectionScaling": true,
      "description": "Good against heavy armored Units",
      "lore": "\"If your hate could be turned into electricity, it would light up the whole world.\" - Nikola",
      "sound": "TeslaWalker_Ready",
      "images": [
          "Tesla_1.png"
      ]
  },

  {
      "tier": 1,
      "rarity": "Common",
      "id": 7,
      "enabled": true,
      "name": "Devil Patrol",
      "race": "Human",
      "cost": 7,
      "type": "Unit",
      "unitType": "DevilPatrol",
      "hp": 70,
      "damage": 5,
      "attackSpeed": 0.5,
      "range": 4,
      "speed": 1.5,
      "accuracy": 1,
      "deflectionRating": 0.2,
      "projectileType": "Energy",
      "elementType": "Ground",
      "targetsAir": false,
      "targetsGround": true,
      "antiMarineBonus": 2,
      "description": "Well protected infantry unit, equipped with a flame thrower that is specialized against infantry.",
      "sound": "Firebat_Ready",
      "images": [
          "1_devilpatrol.png"
      ]
  },

  {
      "tier": 1,
      "rarity": "Common",
      "id": 40,
      "enabled": true,
      "name": "Hot Hand",
      "race": "Human",
      "cost": 6,
      "type": "Unit",
      "unitType": "HotHand",
      "hp": 40,
      "damage": 30,
      "attackSpeed": 1,
      "range": 1,
      "speed": 3.75,
      "accuracy": 1,
      "deflectionRating": 0.1,
      "projectileType": "Melee",
      "elementType": "Ground",
      "targetsAir": false,
      "targetsGround": true,
      "cannotAttackBuildings": false,
      "attributes": [
          "Biological",
          "Light"
      ],
      "description": "Nimble, fast unit that's hard to hit, dealing melee damage",
      "cardText": "Nimble, fast unit that's hard to hit, dealing melee damage",
      "lore": "NSFW",
      "sound": "HotHand_Ready",
      "images": [
          "1_hothand.png"
      ]
  },

  {
      "tier": 1,
      "rarity": "Common",
      "id": 42,
      "enabled": true,
      "name": "Pink Beret",
      "race": "Human",
      "cost": 9,
      "type": "Unit",
      "unitType": "PinkBeret",
      "hp": 60,
      "damage": 10,
      "attackSpeed": 0.5,
      "range": 4,
      "speed": 3,
      "accuracy": 0.7,
      "deflectionRating": 0.1,
      "projectileType": "Bullet",
      "elementType": "Ground",
      "targetsAir": true,
      "targetsGround": true,
      "cannotAttackBuildings": false,
      "attributes": [
          "Biological",
          "Light"
      ],
      "description": "Soldier with one rocket and many bullets",
      "cardText": "Soldier with one rocket and many bullets",
      "shortDescription": "Slick Daddy Soldier",
      "lore": "The army's pride",
      "sound": "PinkBeret_Ready",
      "images": [
          "1_pinkberet.png"
      ]
  },

  {
      "tier": 1,
      "rarity": "Common",
      "id": 43,
      "enabled": true,
      "name": "Hacker",
      "race": "Human",
      "cost": 14,
      "type": "Unit",
      "unitType": "Hacker",
      "hp": 35,
      "damage": 0,
      "speed": 2,
      "accuracy": 1,
      "deflectionRating": 0.1,
      "projectileType": "Energy",
      "elementType": "Ground",
      "targetsAir": false,
      "targetsGround": false,
      "canAttack": false,
      "cannotAttackBuildings": true,
      "attributes": [
          "Biological",
          "Mechanical",
          "Light"
      ],
      "description": "Disables one enemy unit and extracts information",
      "cardText": "Disables one enemy unit and extracts information",
      "shortDescription": "Unit that can manipulate any system",
      "lore": "\"There's a difference between knowing the path and walking the path.\" - Morpheus",
      "sound": "Hacker_Ready",
      "images": [
          "1_hacker.png"
      ]
  },

  {
      "tier": 1,
      "rarity": "Uncommon",
      "id": 10,
      "enabled": true,
      "name": "Wheelie Bomber",
      "race": "Human",
      "cost": 10,
      "type": "Unit",
      "unitType": "WheelieBomber",
      "hp": 50,
      "damage": 150,
      "attackSpeed": 0.2,
      "range": 0.5,
      "scoutRange": 12,
      "scoutLateralRange": 6.5,
      "speed": 10,
      "accuracy": 1,
      "deflectionRating": 0.1,
      "projectileType": "Energy",
      "elementType": "Ground",
      "targetsAir": false,
      "targetsGround": true,
      "isKamikaze": true,
      "splashRadius": 2.5,
      "targetAccuracyDebuff": 0.5,
      "description": "Suicide bomber on an motorcycle, dealing massive damage to ground units",
      "lore": "\"All my life people have been waiting around to see me die.\" - Evel",
      "sound": "Vulture_Ready",
      "images": [
          "1_wheeliebomber.png"
      ]
  },

  {
      "tier": 2,
      "rarity": "Uncommon",
      "id": 2,
      "enabled": true,
      "name": "Panzer",
      "race": "Human",
      "cost": 20,
      "type": "Unit",
      "unitType": "Panzer",
      "hp": 175,
      "damage": 40,
      "attackSpeed": 3.2,
      "range": 11,
      "speed": 2,
      "accuracy": 0.8,
      "deflectionRating": 0.3,
      "projectileType": "Grenade",
      "elementType": "Ground",
      "splashRadius": 2.5,
      "targetsAir": false,
      "targetsGround": true,
      "description": "Heavy mechanized unit, dealing splash damage",
      "sound": "SiegeTank_Ready",
      "images": [
          "Siege_Tank_1.png"
      ]
  },

  {
      "tier": 2,
      "rarity": "Uncommon",
      "id": 4,
      "enabled": true,
      "name": "Rocketship",
      "race": "Human",
      "cost": 15,
      "type": "Unit",
      "unitType": "Rocketship",
      "hp": 100,
      "damage": 30,
      "attackSpeed": 1,
      "range": 10,
      "speed": 3,
      "accuracy": 0.8,
      "deflectionRating": 0.1,
      "projectileType": "Rocket",
      "elementType": "Air",
      "isAir": true,
      "targetsAir": true,
      "targetsGround": true,
      "dualMissile": true,
      "description": "Air Unit shooting many rockets",
      "sound": "Wraith_Ready",
      "images": [
          "1_Rocketship.png"
      ]
  },

  {
      "tier": 2,
      "rarity": "Uncommon",
      "id": 5,
      "enabled": true,
      "name": "Deployable Cannon",
      "race": "Human",
      "cost": 40,
      "type": "Structure",
      "unitType": "DeployableCannon",
      "isStructure": true,
      "hp": 800,
      "damage": 10,
      "attackSpeed": 1,
      "range": 10,
      "speed": 0,
      "accuracy": 0.7,
      "deflectionRating": 0.5,
      "projectileType": "Grenade",
      "elementType": "Ground",
      "reloadSkipCount": 4,
      "deployAtBonusIndicator": true,
      "statsText": "Structure",
      "description": "Stationary cannon, helping to hold the front line",
      "sound": "SiegeTank_Ready",
      "images": [
          "1_Cannon.png"
      ]
  },

  {
      "tier": 2,
      "rarity": "Uncommon",
      "id": 8,
      "enabled": true,
      "name": "Shade",
      "race": "Human",
      "cost": 10,
      "type": "Unit",
      "unitType": "Shade",
      "hp": 40,
      "damage": 25,
      "attackSpeed": 3,
      "range": 20,
      "speed": 2,
      "accuracy": 1,
      "deflectionRating": 0,
      "projectileType": "Bullet",
      "elementType": "Ground",
      "isCloaked": true,
      "description": "Hidden long range infantry specialized on precise operations.",
      "sound": "Ghost_Ready",
      "images": [
          "1_shade.png"
      ]
  },

  {
      "tier": 2,
      "rarity": "Uncommon",
      "id": 11,
      "enabled": true,
      "name": "A9 Piranha",
      "race": "Human",
      "cost": 20,
      "type": "Unit",
      "unitType": "A9Piranha",
      "hp": 150,
      "damage": 1,
      "attackSpeed": 0.1,
      "range": 12,
      "speed": 2.5,
      "accuracy": 0.7,
      "deflectionRating": 0.2,
      "projectileType": "Bullet",
      "elementType": "Air",
      "isAir": true,
      "dualWeapon": true,
      "burstDuration": 4.0,
      "reloadDuration": 1.0,
      "ejectPilotOnDeath": true,
      "targetsAir": true,
      "targetsGround": true,
      "description": "Heavy attack helicopter. Pilot is trained Marine and will leave the destroyed vehicle with parachute.",
      "lore": "\"People eat fish, Grogan. Fish don't eat people.\" - Paul",
      "sound": "Wraith_Ready",
      "images": [
          "1_a9piranha.png"
      ]
  },

  {
      "tier": 2,
      "rarity": "Uncommon",
      "id": 35,
      "enabled": true,
      "name": "A9 Grizzly",
      "race": "Human",
      "cost": 20,
      "type": "Unit",
      "unitType": "A9Grizzly",
      "hp": 500,
      "damage": 1,
      "attackSpeed": 0.1,
      "range": 15,
      "speed": 1,
      "accuracy": 0.7,
      "deflectionRating": 0.4,
      "projectileType": "Bullet",
      "elementType": "Ground",
      "targetsAir": true,
      "targetsGround": true,
      "detectsCloak": true,
      "globalDetector": true,
      "burstDuration": 4.0,
      "reloadDuration": 2.0,
      "shortDescription": "Well armored exploration vehicle",
      "description": "Well armored exploration vehicle. Can detect cloaked units.",
      "lore": "\"The grizzly bear is six to eight hundred pounds of smugness. It has no need to hide.\" - Craig",
      "sound": "SiegeTank_Ready",
      "images": [
          "1_a9grizzly.png"
      ]
  },

  {
      "tier": 2,
      "rarity": "Uncommon",
      "id": 47,
      "enabled": true,
      "name": "A9 Pigeon",
      "race": "Human",
      "cost": 30,
      "type": "Unit",
      "unitType": "A9Pigeon",
      "hp": 200,
      "damage": 3,
      "attackSpeed": 0.2,
      "range": 13,
      "speed": 3,
      "reverseSpeed": 1,
      "accuracy": 0.7,
      "deflectionRating": 0.2,
      "projectileType": "Bullet",
      "elementType": "Air",
      "isAir": true,
      "targetsAir": true,
      "targetsGround": true,
      "hasGroundBomb": true,
      "bombDamage": 100,
      "bombSplashRadius": 65,
      "attributes": [
          "Mechanical"
      ],
      "shortDescription": "Heavy fighter plane",
      "description": "Heavy fighter plane, equipped with one strong bomb against ground and a gatling gun",
      "lore": "Sometimes you are the pigeon, sometimes you are the statue",
      "sound": "Wraith_Ready",
      "images": [
          "1_a9pigeon.png"
      ]
  },

  {
      "tier": 2,
      "rarity": "Uncommon",
      "id": 33,
      "enabled": true,
      "name": "Fodder",
      "race": "Human",
      "cost": 50,
      "type": "Spell",
      "unitType": "Fodder",
      "isSpell": true,
      "spellEffect": "fodder",
      "description": "Parachutes 8 marines right into battle",
      "shortDescription": "Parachute 8 marines right into battle",
      "lore": "Utrinque Paratus",
      "statsText": "Spell",
      "images": [
          "1_fodder.png"
      ]
  },

  {
      "tier": 3,
      "rarity": "Epic",
      "id": 9,
      "enabled": true,
      "name": "Reconsider",
      "race": "Human",
      "cost": 15,
      "type": "Spell",
      "unitType": "Reconsider",
      "isSpell": true,
      "spellEffect": "concentration",
      "description": "Discard your hand, draw five new cards.",
      "statsText": "Spell",
      "images": [
          "1_concentrate.png"
      ]
  },

  {
      "tier": 3,
      "rarity": "Epic",
      "id": 34,
      "enabled": true,
      "name": "Mental Extraction",
      "race": "Human",
      "cost": 12,
      "type": "Spell",
      "unitType": "MentalExtraction",
      "isSpell": true,
      "spellEffect": "mental_extraction",
      "statsText": "Spell",
      "description": "Discard your hand and replace it with the cards in your opponents hand.",
      "cardText": "Discard your hand and replace it with copies of the cards in your opponents hand. If you play against two opponents, get five random cards from your opponents hands.",
      "lore": "\"Three may keep a secret, if two of them are dead,\" - Benjamin",
      "images": [
          "1_mentalextraction.png"
      ]
  },

  {
      "tier": 3,
      "rarity": "Epic",
      "id": 40,
      "enabled": true,
      "name": "Invasion Capsule Sling",
      "race": "Human",
      "cost": 30,
      "type": "Module",
      "isModule": true,
      "moduleId": "capsule_sling",
      "hp": 800,
      "capsuleDamage": 40,
      "capsuleSplashRadius": 2.5,
      "capsuleUnit": "Marine",
      "capsuleLoadCount": 3,
      "statsText": "Module",
      "description": "Loads up all marines you play. Every three marines loaded, shoots a capsule to the front line",
      "images": [
          "2_slingmodul.png"
      ]
  },

  {
      "tier": 3,
      "rarity": "Epic",
      "id": 32,
      "enabled": true,
      "name": "Drone Command",
      "race": "Human",
      "cost": 50,
      "type": "Module",
      "isModule": true,
      "moduleId": "drone_command",
      "hp": 600,
      "damage": 20,
      "droneDamage": 20,
      "droneInterval": 10,
      "droneRange": 50,
      "droneSpeed": 10,
      "droneSplashRadius": 1.8,
      "statsText": "Module",
      "description": "Emits an explosive suicide drone every 10 seconds. Flies to the nearest ground target in range and explodes.",
      "lore": "Just a toy.",
      "images": [
          "2_dronecommand.png"
      ]
  },

  {
      "tier": 3,
      "rarity": "Rare",
      "id": 44,
      "enabled": true,
      "name": "Congress Module",
      "race": "Human",
      "cost": 80,
      "type": "Module",
      "isModule": true,
      "moduleId": "congress_module",
      "hp": 300,
      "statsText": "Module",
      "description": "Cycles through 5 global decrees every 20s: +2 Dmg, +0.5 Plasma, +1.5 Speed, +10% Deflect, Free Draw.",
      "cardText": "Democraticly votes for or against buffs you receive on the battlefield. Ronald Pump, CEO of the world government company WGC resolves buffs for it's Army via decrees, only to get them withdrawn by congress shortly after.",
      "lore": "Module that brings democracy to the Battlefield.",
      "images": [
          "1_congressmodule.png"
      ]
  },

  {
      "tier": 3,
      "rarity": "Epic",
      "id": 12,
      "enabled": true,
      "name": "The Salvage Company",
      "race": "Human",
      "cost": 50,
      "type": "Unit",
      "unitType": "TheSalvageCompany",
      "hp": 800,
      "damage": 0,
      "speed": 0.2,
      "deflectionRating": 0.5,
      "elementType": "Ground",
      "targetsAir": false,
      "targetsGround": false,
      "description": "Salvages all material of destroyed units on the battlefield to give you 10% of cost in return.",
      "lore": "\"Every day we reconstruct ourselves out of the salvage of our yesterdays\" - James",
      "sound": "SiegeTank_Ready",
      "images": [
          "1_thesalvagecompany.png"
      ]
  },

  {
      "tier": 3,
      "rarity": "Epic",
      "id": 14,
      "enabled": true,
      "name": "Orchestra",
      "race": "Human",
      "cost": 50,
      "type": "Unit",
      "unitType": "Orchestra",
      "hp": 100,
      "damage": 50,
      "attackSpeed": 1,
      "range": 50,
      "speed": 1,
      "accuracy": 0.2,
      "deflectionRating": 0.1,
      "projectileType": "Rocket",
      "elementType": "Ground",
      "splashRadius": 2.5,
      "targetsAir": false,
      "targetsGround": true,
      "reloadDuration": 10.0,
      "attributes": [
          "Mechanical",
          "Armored"
      ],
      "description": "Heavy long range artillery, shooting unprecise barrages of rockets at ground units and structures.",
      "lore": "\"Die Artillerie ist der Gott des Krieges.\" - Joseph",
      "sound": "SiegeTank_Ready",
      "images": [
          "1_orchestra.png"
      ]
  },

  {
      "tier": 3,
      "rarity": "Epic",
      "id": 39,
      "enabled": true,
      "name": "Stickman",
      "race": "Human",
      "cost": 33,
      "type": "Unit",
      "unitType": "Stickman",
      "hp": 180,
      "damage": 80,
      "attackSpeed": 5,
      "range": 18,
      "speed": 1.5,
      "accuracy": 0.9,
      "deflectionRating": 0.2,
      "projectileType": "Grenade",
      "elementType": "Ground",
      "splashRadius": 24,
      "splashDamagePercent": 0.5,
      "targetsAir": true,
      "targetsGround": true,
      "attributes": [
          "Mechanical",
          "Massive"
      ],
      "description": "Heavy long-range walker. Halved range vs Air. Collapses into an indestructible obstacle on death.",
      "lore": "\"Stickman, oh Stickman, beware of the swan!\"",
      "sound": "SiegeTank_Ready",
      "images": [
          "1_stickman.png"
      ]
  },

  {
      "tier": 1,
      "rarity": "Common",
      "id": 15,
      "enabled": true,
      "name": "Defective Loaf",
      "race": "Sul",
      "caste": "Loaf",
      "cost": 7,
      "type": "Unit",
      "unitType": "DefectiveLoaf",
      "hp": 60,
      "damage": 5,
      "attackSpeed": 0.5,
      "range": 2,
      "speed": 4,
      "accuracy": 1,
      "deflectionRating": 0.3,
      "projectileType": "Melee",
      "elementType": "Ground",
      "hasTether": true,
      "tetherDps": 20,
      "hasDeathAcid": true,
      "description": "Primitive loaf unit, that needs to get close to enemy unit. Deals damage over time.",
      "lore": "Incomplete. Abandon.",
      "sound": "Sul_Ready",
      "images": [
          "1_defectiveloaf.png"
      ]
  },

  {
      "tier": 1,
      "rarity": "Common",
      "id": 16,
      "enabled": true,
      "name": "Rejected Loaf",
      "race": "Sul",
      "caste": "Loaf",
      "cost": 7,
      "type": "Unit",
      "unitType": "RejectedLoaf",
      "hp": 80,
      "damage": 0,
      "attackSpeed": 1,
      "range": 0,
      "speed": 1,
      "accuracy": 1,
      "deflectionRating": 0.5,
      "projectileType": "Bullet",
      "elementType": "Ground",
      "targetsAir": false,
      "targetsGround": false,
      "canAttack": false,
      "reflectDamageRatio": 0.5,
      "description": "Can't attack. Reflects 50% of damage taken back to opponent unit that deals the damage.",
      "lore": "Insufficient. Abandon.",
      "sound": "Sul_Ready",
      "images": [
          "1_rejectedloaf.png"
      ]
  },

  {
      "tier": 1,
      "rarity": "Common",
      "id": 17,
      "enabled": true,
      "name": "Apostate Loaf",
      "race": "Sul",
      "caste": "Loaf",
      "cost": 5,
      "type": "Unit",
      "unitType": "ApostateLoaf",
      "hp": 50,
      "damage": 20,
      "attackSpeed": 1.0,
      "range": 10,
      "speed": 2,
      "accuracy": 0.6,
      "deflectionRating": 0.1,
      "projectileType": "Grenade",
      "elementType": "Ground",
      "targetsAir": false,
      "targetsGround": true,
      "sporeSlow": { "speedReduction": 2, "duration": 10 },
      "description": "Small artillery unit shooting slowing clumps of spores",
      "lore": "None of us. Abandon.",
      "sound": "Sul_Ready",
      "images": [
          "1_apostateloaf.png"
      ]
  },

  {
      "tier": 1,
      "rarity": "Common",
      "id": 41,
      "enabled": true,
      "name": "Forgotten Loaf",
      "race": "Sul",
      "caste": "Loaf",
      "cost": 6,
      "type": "Unit",
      "unitType": "ForgottenLoaf",
      "hp": 60,
      "damage": 70,
      "attackSpeed": 1.0,
      "range": 4,
      "speed": 8,
      "accuracy": 1,
      "deflectionRating": 0.5,
      "projectileType": "Grenade",
      "elementType": "Ground",
      "targetsAir": true,
      "targetsGround": true,
      "canAttack": false,
      "isKamikaze": true,
      "splashRadius": 4,
      "description": "Walking Mine, that burries itself visible at the frontline.",
      "shortDescription": "Spider Mine",
      "cardText": "Walking Mine, that burries itself visible at the frontline.",
      "lore": "Who? Abandon",
      "sound": "Sul_Ready",
      "images": [
          "1_forgottenloaf.png"
      ]
  },

  {
      "tier": 2,
      "rarity": "Uncommon",
      "id": 18,
      "enabled": true,
      "name": "Delving Pillar",
      "race": "Sul",
      "caste": "Pillar",
      "cost": 10,
      "type": "Unit",
      "unitType": "DelvingPillar",
      "hp": 90,
      "damage": 40,
      "attackSpeed": 10.0,
      "range": 20,
      "speed": 3,
      "accuracy": 0.9,
      "deflectionRating": 0.2,
      "projectileType": "Rocket",
      "elementType": "Air",
      "isAir": true,
      "targetsAir": false,
      "targetsGround": true,
      "detectsCloak": true,
      "detectCloakRange": 30,
      "disarmOnHit": { "duration": 3 },
      "description": "Long range air unit, that can detect cloaked enemy units and blind targets. Can only attack ground units.",
      "lore": "When they first arrive in the void, the void is not void anymore.",
      "sound": "Sul_Ready",
      "images": [
          "1_delvingpillar.png"
      ]
  },

  {
      "tier": 2,
      "rarity": "Uncommon",
      "id": 19,
      "enabled": true,
      "name": "Harvesting Pillar",
      "race": "Sul",
      "caste": "Pillar",
      "cost": 15,
      "type": "Unit",
      "unitType": "HarvestingPillar",
      "hp": 170,
      "damage": 7,
      "attackSpeed": 0.5,
      "range": 5,
      "speed": 2,
      "accuracy": 1,
      "deflectionRating": 0.3,
      "projectileType": "Melee",
      "elementType": "Ground",
      "targetsAir": true,
      "targetsGround": true,
      "hasDualTether": true,
      "dualTetherDps": 20,
      "dualTetherPhase2Delay": 5.0,
      "dualTetherLifestealRatio": 0.1,
      "description": "Unit that needs to connect it's tentacles to enemy unit to deal damage and restore health. Can attack ground and air units.",
      "lore": "Blood Plasma is also Plasma.",
      "sound": "Sul_Ready",
      "images": [
          "1_harvestingpillar.png"
      ]
  },

  {
      "tier": 2,
      "rarity": "Uncommon",
      "id": 20,
      "enabled": true,
      "name": "Processing Pillar",
      "race": "Sul",
      "caste": "Pillar",
      "cost": 10,
      "type": "Unit",
      "unitType": "ProcessingPillar",
      "hp": 90,
      "damage": 40,
      "attackSpeed": 5.0,
      "range": 8,
      "speed": 2,
      "accuracy": 0.9,
      "deflectionRating": 0.1,
      "projectileType": "Energy",
      "elementType": "Ground",
      "isAir": false,
      "targetsAir": true,
      "targetsGround": true,
      "prioritizeGround": true,
      "splashRadius": 2.1,
      "description": "Unit creating different projectiles, depending if it attacks ground or air units",
      "lore": "Pillar, the creator",
      "sound": "Sul_Ready",
      "images": [
          "1_processingpillar.png"
      ]
  },

  {
      "tier": 2,
      "rarity": "Uncommon",
      "id": 38,
      "enabled": true,
      "name": "Claiming Pillar",
      "race": "Sul",
      "caste": "Pillar",
      "cost": 10,
      "type": "Structure",
      "unitType": "ClaimingPillar",
      "isStructure": true,
      "hp": 200,
      "damage": 0,
      "speed": 0,
      "accuracy": 1,
      "deflectionRating": 0.2,
      "projectileType": "Melee",
      "elementType": "Ground",
      "canAttack": false,
      "deployAtBonusIndicator": true,
      "growthDuration": 3.0,
      "hpRegenRate": 2.0,
      "deathBurstDamage": 30,
      "deathBurstRange": 8,
      "statsText": "Structure",
      "description": "Structure that controls the bonus level indicator",
      "cardText": "Structure that holds the bonus level indicator, doubling it's bonus income. Regenerates health.",
      "lore": "Pollute it until no one wants to have it.",
      "sound": "Sul_Ready",
      "images": [
          "1_claimingpillar.png"
      ]
  },

  {
      "tier": 2,
      "rarity": "Rare",
      "id": 21,
      "enabled": true,
      "name": "Preserver Lock",
      "race": "Sul",
      "caste": "Lock",
      "cost": 20,
      "type": "Unit",
      "unitType": "PreserverLock",
      "hp": 200,
      "damage": 2,
      "attackSpeed": 0.1,
      "range": 9,
      "speed": 1.5,
      "accuracy": 1,
      "deflectionRating": 0.3,
      "projectileType": "Energy",
      "elementType": "Ground",
      "targetsAir": false,
      "targetsGround": true,
      "hasBeamTether": true,
      "description": "Well armored unit, that deals damage to ground units with a devastating plasma beam.",
      "lore": "Night fever, night feveeeeeer...",
      "sound": "Sul_Ready",
      "images": [
          "1_preserverlock.png"
      ]
  },

  {
      "tier": 2,
      "rarity": "Rare",
      "id": 22,
      "enabled": true,
      "name": "Sentinal Lock",
      "race": "Sul",
      "caste": "Lock",
      "cost": 20,
      "type": "Unit",
      "unitType": "SentinalLock",
      "hp": 120,
      "damage": 2,
      "attackSpeed": 0.2,
      "range": 12,
      "speed": 3,
      "accuracy": 0.7,
      "deflectionRating": 0.1,
      "projectileType": "Bullet",
      "elementType": "Air",
      "isAir": true,
      "targetsAir": true,
      "targetsGround": true,
      "description": "Allround air fighter",
      "lore": "Humens named it \"Spitfire\".",
      "sound": "Sul_Ready",
      "images": [
          "1_sentinallock.png"
      ]
  },

  {
      "tier": 2,
      "rarity": "Rare",
      "id": 45,
      "enabled": true,
      "name": "Bastion Lock",
      "race": "Sul",
      "caste": "Lock",
      "cost": 20,
      "type": "Unit",
      "unitType": "BastionLock",
      "hp": 400,
      "damage": 1,
      "attackSpeed": 0.1,
      "range": 22,
      "speed": 1.5,
      "accuracy": 0.8,
      "deflectionRating": 0.2,
      "projectileType": "Bullet",
      "elementType": "Ground",
      "targetsAir": false,
      "targetsGround": true,
      "splashRadius": 28,
      "splashDamage": 1,
      "description": "Well armored unit, that deploys a shield while stationary.",
      "shortDescription": "Very tanky Sul unit",
      "cardText": "Well armored unit, that deploys a shield while stationary.",
      "lore": "\"This is where we hold them! This is where we fight! This is where they die!\" - Leonidas",
      "sound": "Sul_Ready",
      "images": [
          "1_bastionlock.png"
      ]
  },

  {
      "tier": 2,
      "rarity": "Rare",
      "id": 23,
      "enabled": true,
      "name": "Monitor Lock",
      "race": "Sul",
      "caste": "Lock",
      "cost": 30,
      "type": "Structure",
      "unitType": "MonitorLock",
      "isStructure": true,
      "hp": 380,
      "damage": 30,
      "attackSpeed": 3,
      "range": 10,
      "speed": 0,
      "accuracy": 1,
      "deflectionRating": 0.4,
      "projectileType": "Melee",
      "elementType": "Ground",
      "deployAtBonusIndicator": true,
      "growthDuration": 3.0,
      "swarmBuffDamage": 0.3,
      "swarmBuffHp": 0.3,
      "description": "A long worm, growing and staying at the place it spawns, buffing friendly units and attacking near enemies.",
      "cardText": "A big worm growing from earth, buffing friendly units and attacking enemies in range.",
      "lore": "„All the war-propaganda, all the screaming and lies and hatred, comes invariably from people who are not fighting.“ -George",
      "sound": "Sul_Ready",
      "images": [
          "1_monitorlock.png"
      ]
  },

  {
      "tier": 3,
      "rarity": "Epic",
      "id": 24,
      "enabled": true,
      "name": "Enforcer Vulture",
      "race": "Sul",
      "caste": "Vulture",
      "cost": 60,
      "type": "Unit",
      "unitType": "EnforcerVulture",
      "hp": 300,
      "damage": 30,
      "attackSpeed": 5,
      "range": 15,
      "speed": 1,
      "accuracy": 0.9,
      "deflectionRating": 0.2,
      "projectileType": "Rocket",
      "elementType": "Air",
      "isAir": true,
      "targetsAir": true,
      "targetsGround": true,
      "attributes": ["Armored", "Massive", "Biological", "Mechanical"],
      "description": "A devastating, manned warmachine.",
      "cardText": "Sul warmachine with a devastating weaponsystem",
      "lore": "\"There is hope and a kind of beauty in there somewhere, if you look for it.\" - Hans Rudolf",
      "sound": "Sul_Ready",
      "images": [
          "1_enforcervulture.png"
      ]
  },

  {
      "tier": 3,
      "rarity": "Epic",
      "id": 25,
      "enabled": true,
      "name": "Looting Vulture",
      "race": "Sul",
      "caste": "Vulture",
      "cost": 50,
      "type": "Unit",
      "unitType": "LootingVulture",
      "hp": 200,
      "damage": 20,
      "attackSpeed": 0.5,
      "range": 15,
      "speed": 0.5,
      "accuracy": 1,
      "deflectionRating": 0.3,
      "projectileType": "Energy",
      "elementType": "Air",
      "isAir": true,
      "targetsAir": true,
      "targetsGround": true,
      "attributes": ["Biological", "Massive"],
      "bountyRatio": 0.5,
      "petrifiesFirstEnemy": true,
      "description": "Receives 50% of killed enemies' plasma cost. Turns the first enemy in range into a permanent indestructible golden statue.",
      "cardText": "The incarnate greed of the swarm",
      "lore": "\"Be fearful when others are greedy and greedy when others are fearful.\" - Warren",
      "sound": "Sul_Ready",
      "images": [
          "1_lootingvulture.png"
      ]
  },

  {
      "tier": 3,
      "rarity": "Epic",
      "id": 26,
      "enabled": true,
      "name": "Parasite Vulture",
      "race": "Sul",
      "caste": "Vulture",
      "cost": 50,
      "type": "Unit",
      "unitType": "ParasiteVulture",
      "hp": 150,
      "damage": 0,
      "attackSpeed": 1.0,
      "range": 10,
      "speed": 5,
      "accuracy": 1,
      "deflectionRating": 0.1,
      "projectileType": "Melee",
      "elementType": "Air",
      "isAir": true,
      "targetsAir": true,
      "targetsGround": true,
      "attributes": ["Biological"],
      "description": "Parasitic unit, that overtakes an enemy unit.",
      "cardText": "Flies to an enemy unit to take control of it.",
      "lore": "You can be whatever you want",
      "sound": "Sul_Ready",
      "images": [
          "1_parasitevulture.png"
      ]
  },

  {
      "tier": 3,
      "rarity": "Epic",
      "id": 35,
      "enabled": true,
      "name": "Anti Shade",
      "race": "Sul",
      "caste": "Vulture",
      "cost": 47,
      "type": "Unit",
      "unitType": "AntiShade",
      "hp": 40,
      "damage": 5,
      "attackSpeed": 0.5,
      "range": 1,
      "speed": 3,
      "accuracy": 1,
      "deflectionRating": 0.0,
      "projectileType": "Melee",
      "elementType": "Ground",
      "targetsAir": false,
      "targetsGround": true,
      "cannotAttackBuildings": true,
      "isCloaked": false,
      "attributes": ["Biological"],
      "description": "Cloaked melee unit that stalks the frontline.",
      "cardText": "Cloaked melee unit, that haunts the battlefield.",
      "lore": "The Sul provoked countless battles, just to farm enough essence of dead shade bodies and armor, enabling them to evolve the Anti Shade.",
      "sound": "Sul_Ready",
      "images": [
          "1_antishade.png"
      ]
  },

  {
      "tier": 2,
      "rarity": "Uncommon",
      "id": 27,
      "enabled": true,
      "name": "Forced Overstimulation",
      "race": "Sul",
      "cost": 17,
      "type": "Spell",
      "unitType": "ForcedOverstimulation",
      "isSpell": true,
      "spellEffect": "forced_overstimulation",
      "statsText": "Spell",
      "description": "For 10 seconds, your existing units gain 2x HP, 2x Attack Speed, 2x Speed, and -0.1 Accuracy. After 10 seconds, affected units die instantly.",
      "cardText": "Send your units on a short frenzy, doubling Attack Speed, Speed and HP, rewarding them with death.",
      "lore": "\"Faster, faster, until the thrill of speed overcomes the fear of death.\" - Hunter S.",
      "sound": "Sul_Ready",
      "images": [
          "1_forcedoverstimulation.png"
      ]
  },

  {
      "tier": 2,
      "rarity": "Uncommon",
      "id": 46,
      "enabled": true,
      "name": "Ambush",
      "race": "Sul",
      "cost": 0,
      "costDisplay": "X",
      "hasVariableCost": true,
      "maxCostCap": 150,
      "type": "Spell",
      "unitType": "Ambush",
      "isSpell": true,
      "spellEffect": "ambush",
      "statsText": "Spell",
      "description": "Cost of this spell is equal to the sum of cost of units in your hand. Spawns all units from your hand right into battle.",
      "shortDescription": "Spawn units from your hand in battle",
      "cardText": "Cost of this spell is equal to the sum of cost of units in your hand. Spawns all units from your hand right into battle.",
      "lore": "You don't survive a trap by being brave.",
      "sound": "Sul_Ready",
      "images": [
          "1_ambush.png"
      ]
  },

  {
      "tier": 3,
      "rarity": "Epic",
      "id": 28,
      "enabled": true,
      "name": "Experimental Metamorphosis",
      "race": "Sul",
      "cost": 33,
      "type": "Spell",
      "unitType": "ExperimentalMetamorphosis",
      "isSpell": true,
      "spellEffect": "experimental_metamorphosis",
      "statsText": "Spell",
      "description": "Turn all units on the battlefield into random Sul units.",
      "cardText": "Turn all units on the battlefield into a cocoon. After 30 seconds, a random sul unit spawns from the cocoon.",
      "lore": "“What's happened to me,' he thought. It was no dream.” - Franz",
      "sound": "Sul_Ready",
      "images": [
          "1_experimentalmetamorphosis.png"
      ]
  },

  {
      "tier": 1,
      "rarity": "Uncommon",
      "id": 37,
      "enabled": true,
      "name": "Precision Strike",
      "race": "Sul",
      "cost": 10,
      "type": "Spell",
      "unitType": "PrecisionStrike",
      "isSpell": true,
      "spellEffect": "precision_strike",
      "statsText": "Spell",
      "description": "Drops a bomb on the enemy unit or structure closest to the bonus indicator.",
      "shortDescription": "Drops a bomb on the enemy unit or structure closest to the bonus indicator.",
      "cardText": "Drops a bomb on the enemy unit or structure closest to the bonus indicator.",
      "lore": "The sun stops shining for half a second. Maybe forever.",
      "sound": "Sul_Ready",
      "images": [
          "1_precisionstrike.png"
      ]
  },

  {
      "tier": 2,
      "rarity": "Epic",
      "id": 49,
      "enabled": true,
      "name": "Retreat Protocol",
      "race": "Sul",
      "cost": 70,
      "type": "Spell",
      "unitType": "RetreatProtocol",
      "isSpell": true,
      "spellEffect": "retreat_protocol",
      "statsText": "Spell",
      "description": "Retreats your units to base, guarded by 2 Bastion Locks. Returning units refund 100% cost.",
      "shortDescription": "Retreats units to base with 2 Bastion Locks. 100% refund.",
      "cardText": "Retreats your units to base, guarded by 2 Bastion Locks. Returning units refund 100% cost.",
      "lore": "\"Peace comes through strength, not through retreat.\" - Kevin",
      "sound": "Sul_Ready",
      "images": [
          "1_retreatprotocol.png"
      ]
  },

  {
      "tier": 3,
      "rarity": "Epic",
      "id": 38,
      "enabled": true,
      "name": "Pulp",
      "race": "Sul",
      "cost": 14,
      "type": "Unit",
      "unitType": "Pulp",
      "hp": 10,
      "damage": 100,
      "attackSpeed": 4,
      "range": 10,
      "speed": 5,
      "accuracy": 0.5,
      "deflectionRating": 0.3,
      "splashRadius": 3,
      "projectileType": "Grenade",
      "elementType": "Ground",
      "targetsGround": true,
      "targetsAir": false,
      "isWildcardDrilling": true,
      "description": "Missing piece for drilling.",
      "shortDescription": "Missing piece for drilling.",
      "cardText": "Can combine with any other two cards for a drilling.",
      "lore": "\"Pencil Trick\"",
      "sound": "Sul_Ready",
      "images": [
          "1_pulp.png"
      ]
  },

  {
      "tier": 2,
      "rarity": "Uncommon",
      "id": 101,
      "enabled": false,
      "name": "Hyper Marine",
      "race": "Human",
      "cost": 15,
      "type": "Melt",
      "unitType": "HyperMarine",
      "hp": 150,
      "damage": 10,
      "attackSpeed": 0.3,
      "range": 6,
      "speed": 3,
      "accuracy": 0.9,
      "deflectionRating": 0.2,
      "projectileType": "Bullet",
      "elementType": "Ground",
      "description": "",
      "lore": "\"Hyper, Hyper\" - H.P.",
      "sound": "Marine_Ready",
      "images": [
          "1_Hypermarine.png"
      ]
  },

  {
      "tier": 2,
      "rarity": "Uncommon",
      "id": 108,
      "enabled": false,
      "name": "Hyper Shade",
      "race": "Human",
      "cost": 20,
      "type": "Melt",
      "unitType": "HyperShade",
      "hp": 100,
      "damage": 40,
      "attackSpeed": 2.5,
      "range": 25,
      "speed": 2.5,
      "accuracy": 1,
      "deflectionRating": 0.1,
      "projectileType": "Bullet",
      "elementType": "Ground",
      "isCloaked": true,
      "hasExplosiveCrow": true,
      "description": "Melt Version Of Shade",
      "sound": "Ghost_Ready",
      "images": [
          "1_hypershade.png"
      ]
  },

  {
      "tier": 1,
      "rarity": "Common",
      "id": 107,
      "enabled": false,
      "name": "Hyper Devil Patrol",
      "race": "Human",
      "cost": 15,
      "type": "Melt",
      "unitType": "HyperDevilPatrol",
      "hp": 200,
      "damage": 20,
      "attackSpeed": 1,
      "range": 9,
      "speed": 3,
      "accuracy": 0.7,
      "deflectionRating": 0.4,
      "projectileType": "Grenade",
      "elementType": "Air",
      "isAir": true,
      "targetsAir": false,
      "targetsGround": true,
      "splashRadius": 2.2,
      "hasIncinerate": true,
      "incinerateDuration": 5,
      "incinerateDps": 2,
      "description": "Well armored flying infantry, shooting grenades that incinerate target and deal splash damage. Can only attack ground units.",
      "lore": "\"Rocket man burnin' out his fuse up here alone\" - Elton",
      "sound": "Firebat_Ready",
      "images": [
          "1_hyperdevilpatrol.png"
      ]
  },

  {
      "tier": 1,
      "rarity": "Common",
      "id": 103,
      "enabled": false,
      "name": "Hyper Tesla Walker",
      "race": "Human",
      "cost": 40,
      "type": "Melt",
      "unitType": "HyperTeslaWalker",
      "hp": 350,
      "damage": 50,
      "attackSpeed": 3,
      "range": 10,
      "speed": 1.5,
      "accuracy": 1,
      "deflectionRating": 0.2,
      "projectileType": "Energy",
      "elementType": "Ground",
      "deflectionScaling": true,
      "description": "Melt version of the Tesla walker",
      "lore": "Drip… Sizzle…",
      "sound": "TeslaWalker_Ready",
      "images": [
          "1_hypertesla.png"
      ]
  },

  {
      "tier": 2,
      "rarity": "Uncommon",
      "id": 102,
      "enabled": false,
      "name": "Hyper Panzer",
      "race": "Human",
      "cost": 50,
      "type": "Melt",
      "unitType": "HyperPanzer",
      "hp": 400,
      "damage": 60,
      "attackSpeed": 2.5,
      "range": 15,
      "speed": 2,
      "accuracy": 0.9,
      "deflectionRating": 0.5,
      "projectileType": "Grenade",
      "elementType": "Ground",
      "splashRadius": 3.0,
      "deathExplosionDamage": 100,
      "deathExplosionRadius": 5.0,
      "description": "Melt version of the Panzer",
      "cardText": "Big, well armored tank, equipped with weapons against ground and air units. Smashes things in it's way.",
      "lore": "\"Nothing crushes freedom as substantially as a tank\" - Shirley",
      "sound": "SiegeTank_Ready",
      "images": [
          "1_hyperpanzer.png"
      ]
  },

  // ============================================================================
  // 1b. KAMPAGNEN-EXKLUSIV (AKTIV IM SPIEL, ABER NIE IN PVP)
  // isCampaignOnly: true - Karten/Einheiten, die ausschließlich in der
  // Singleplayer-Kampagne gespawnt werden (nie über Deckbau, Booster,
  // Matchmaking oder Online-Decks erreichbar - siehe getActiveCards()
  // unten, slotMachine.js's _unitsPool-Filter und server/deckValidator.js).
  // enabled:true, da sie in der Kampagnen-Simulation ganz normal aktiv sind.
  // Siehe docs/game-design-singleplayer-kampagne.md Kapitel 3.
  // ============================================================================
  {
      "tier": 1,
      "rarity": "Common",
      "id": 900,
      "enabled": true,
      "isCampaignOnly": true,
      "name": "Black Marine",
      "race": "Human",
      "cost": 5,
      "type": "Unit",
      "unitType": "Marine",
      "spriteRecolor": "black",
      "hp": 45,
      "damage": 6,
      "attackSpeed": 0.4,
      "range": 5,
      "speed": 2.25,
      "accuracy": 0.8,
      "deflectionRating": 0,
      "projectileType": "Bullet",
      "elementType": "Ground",
      "description": "Kampagnen-exklusive Wacheinheit. Kampfwerte identisch zum regulären Marine; wird pro Encounter per hpOverride angepasst (siehe js/data/campaignMissions.js).",
      "sound": "Marine_Ready",
      "images": [
          "Marine_1.png"
      ]
  }
];


// Fast O(1) indexed lookup tables (Paradigm 1 & 7: zero linear scan overhead)
const _CARDS_BY_NAME_LOWER = new Map();
const _CARDS_BY_UNIT_TYPE = new Map();
const _CARDS_BY_MODULE_ID = new Map();

for (let i = 0; i < CARDS_DATA.length; i++) {
  const c = CARDS_DATA[i];
  if (c.name) {
    _CARDS_BY_NAME_LOWER.set(c.name.toLowerCase(), c);
  }
  if (c.unitType && !_CARDS_BY_UNIT_TYPE.has(c.unitType)) {
    _CARDS_BY_UNIT_TYPE.set(c.unitType, c);
  }
  if (c.moduleId && !_CARDS_BY_MODULE_ID.has(c.moduleId)) {
    _CARDS_BY_MODULE_ID.set(c.moduleId, c);
  }
}

export function getCardByName(name) {
  if (!name) return null;
  return _CARDS_BY_NAME_LOWER.get(String(name).toLowerCase()) || null;
}

export function getCardByUnitType(unitType) {
  if (!unitType) return null;
  return _CARDS_BY_UNIT_TYPE.get(unitType) || null;
}

export function getCardByModuleId(moduleId) {
  if (!moduleId) return null;
  return _CARDS_BY_MODULE_ID.get(moduleId) || null;
}

// Neustart "Marine Null": every card carries an `enabled` flag. Only
// enabled cards are drawable, playable, or selectable in the deck
// builder - this is the single place that filters on it, so re-enabling a
// card later is a one-character change here plus in the card's own entry
// above. See NEUSTART_PLAN.html Phase 1.
// isCampaignOnly cards are enabled:true (they're simulated normally inside
// a campaign mission) but must never reach PvP deck building, boosters, or
// matchmaking - excluded here so every consumer of getActiveCards() gets
// that for free. Campaign code fetches them directly via getCardByName()
// instead. See docs/game-design-singleplayer-kampagne.md Kapitel 3.
export function getActiveCards() {
  return CARDS_DATA.filter(c => c.enabled === true && !c.isCampaignOnly);
}

export function isCardActive(name) {
  const card = getCardByName(name);
  return !!card && card.enabled === true;
}

export const ASSET_VERSION = BALANCE_HASH;

/**
 * Exact mapping of card names to dedicated gold edition artwork files in assets/cards/
 */
export const GOLD_CARD_IMAGES = {
  'Marine': 'gold_marine.png',
  'Tesla Walker': 'gold_tesla.png',
  'Devil Patrol': 'gold_devilpatrol.png',
  'Wheelie Bomber': 'gold_wheeliebomber.png',
  'Panzer': 'gold_siegetank.png',
  'Rocketship': 'gold_rocketship.png',
  'Shade': 'gold_shade.png',
  'A9 Piranha': 'gold_a9piranha.png',
  'A9 Grizzly': 'gold_a9grizzly.png',
  'A9 Pigeon': 'gold_a9pigeon.png',
  'Reconsider': 'gold_concentrate.png',
  'Invasion Capsule Sling': 'gold_2_slingmodul.png',
  'The Salvage Company': 'gold_thesalvagecompany.png',
  'Orchestra': 'gold_orchestra.png',
  'Defective Loaf': 'gold_defectiveloaf.png',
  'Rejected Loaf': 'gold_rejectedloaf.png',
  'Apostate Loaf': 'gold_apostateloaf.png',
  'Delving Pillar': 'gold_delvingpillar.png',
  'Harvesting Pillar': 'gold_harvestingpillar.png',
  'Processing Pillar': 'gold_processingpillar.png',
  'Claiming Pillar': 'gold_claimingpillar.png',
  'Preserver Lock': 'gold_preserverlock.png',
  'Bastion Lock': 'gold_bastionlock.png',
  'Sentinal Lock': 'gold_sentinallock.png',
  'Monitor Lock': 'gold_monitorlock.png',
  'Enforcer Vulture': 'gold_enforcervulture.png',
  'Looting Vulture': 'gold_lootingvulture.png',
  'Parasite Vulture': 'gold_parasitevulture.png',
  'Hyper Marine': 'gold_hypermarine.png',
  'Hyper Shade': 'gold_hypershade.png',
  'Hyper Devil Patrol': 'gold_hyperdevilpatrol.png',
  'Hyper Tesla Walker': 'gold_hypertesla.png',
  'Hyper Panzer': 'gold_hyperpanzer.png',
  'Black Marine': 'gold_marine.png',
  'Deployable Cannon': 'gold_cannon.png',
  'Drone Command': 'gold_2_dronecommand.png',
  'Fodder': 'gold_fodder.png',
  'Mental Extraction': 'gold_mentalextraction.png',
  'Anti Shade': 'gold_antishade.png',
  'Ambush': 'gold_ambush.png',
  'Forced Overstimulation': 'gold_forcedoverstimulation.png',
  'Experimental Metamorphosis': 'gold_experimentalmetamorphosis.png',
  'Precision Strike': 'gold_precisionstrike.png',
  'Retreat Protocol': 'gold_retreatprotocol.png',
  'Pulp': 'gold_pulp.png',
  'Stickman': 'gold_stickman.png',
  'Hot Hand': 'gold_hothand.png',
  'Pink Beret': 'gold_pinkberet.png',
  'Forgotten Loaf': 'gold_forgottenloaf.png',
  'Hacker': 'gold_hacker.png',
  'Congress Module': 'gold_congressmodule.png'
};

/**
 * Returns the gold edition artwork filename for a card name, or null if none is available.
 * Checks explicit mapping in GOLD_CARD_IMAGES first, then automatically derives from the
 * card's primary image (e.g. "1_test.png" -> "gold_test.png", "test.png" -> "gold_test.png").
 *
 * @param {string} cardName
 * @returns {string|null}
 */
export function getGoldCardImage(cardName) {
  if (!cardName) return null;
  if (GOLD_CARD_IMAGES[cardName]) {
    return GOLD_CARD_IMAGES[cardName];
  }

  // Automatic derivation for new units (e.g. "1_test.png" -> "gold_test.png")
  const card = CARDS_DATA.find(c => c.name === cardName || c.unitType === cardName);
  const primaryImg = card?.images?.[0] || (typeof cardName === 'string' && cardName.includes('.') ? cardName : null);
  if (!primaryImg) return null;

  const lowerImg = primaryImg.toLowerCase();
  if (lowerImg.startsWith('gold_')) {
    return lowerImg;
  }

  const cleanName = lowerImg.replace(/^\d+_/, '');
  const baseWithoutExt = cleanName.replace(/\.[^.]+$/, '');
  return `gold_${baseWithoutExt}.png`;
}

/**
 * Resolves a card image filename to a cache-busted path.
 * Appends ?v=${ASSET_VERSION} so browser cache is automatically invalidated
 * whenever game assets or balance are updated.
 *
 * @param {string} imageName - Filename under assets/cards/
 * @returns {string} Versioned relative asset path
 */
export function getCardImageUrl(imageName) {
  const file = imageName || 'Marine_1.png';
  return `assets/cards/${file}?v=${ASSET_VERSION}`;
}

let _profileArtworkCardsCache = null;
export function getProfileArtworkCards() {
  if (_profileArtworkCardsCache) return _profileArtworkCardsCache;
  _profileArtworkCardsCache = CARDS_DATA.filter(c =>
    (c.enabled === true || c.type === 'Melt') && !c.isCampaignOnly && c.images && c.images[0]
  ).sort((a, b) => a.name.localeCompare(b.name));
  return _profileArtworkCardsCache;
}


